import { memo, useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Popconfirm,
  Typography,
  Form,
  Input,
  notification,
  Card,
  Space,
  Alert,
  Skeleton,
  Descriptions,
  Row,
  Col,
  Checkbox,
  Modal,
  InputNumber,
} from 'antd';

import BreadcrumbContainer from '../../../containers/Breadcrumb';
import CustomButton from '../../../components/CustomButton';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import MESSAGES from '../../../enums/Messages';
import {
  post,
  patch,
  remove,
  filter as filterAjaxTable,
  swap,
  info,
  preview,
  patchModule,
  renumerate,
  move,
} from '../../../api/module/ctm_responsibilities';

import '../Form/style.scss';
import AjaxTable from '../../../components/AjaxTable';
import { catchException } from '../../../utils/helpers/helpers';
import ModificationModuleSaveButton from '../../../components/ModificationComponents';
import EllipsisModal from '../../../components/EllipsisModal';
import {
  DeleteFilled,
  EditFilled,
  FullscreenOutlined,
  LoadingOutlined,
  SwapLeftOutlined,
  SwapOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import IconColors from '../../../enums/IconColors';
import { IconButton } from '../../../components';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const ResponsibilitiesList = ({
  handleEdit,
  deleteDataSource,
  ctmmID,
  reload,
  onMove,
  moveID,
  onSwap,
  swapID,
  lSwap,
  requireElimination,
  unrequireElimination,
}) => {
  const [deleteID, setDeleteID] = useState();

  const columns = [
    {
      title: 'Código vigente',
      dataIndex: 'codigo',
      key: 'codigo',
      width: '5rem',
      align: 'center',
    },
    {
      title: 'Obligación vigente',
      dataIndex: 'descripcion_vigente',
      key: 'descripcion',
      render: (_, record) => (
        <EllipsisModal
          text={
            record.origen !== 3
              ? record.descripcion_vigente
              : record.descripcion
          }
          title="Obligación vigente"
        />
      ),
    },
    {
      title: 'Solicitar eliminación',
      dataIndex: 'requiere_eliminacion',
      width: '7rem',
      render: (_, record) => (
        <>
          {record.origen !== 3 && (
            <Checkbox
              checked={record.requiere_eliminacion}
              onClick={() =>
                record.requiere_eliminacion
                  ? unrequireElimination(record)
                  : requireElimination(record)
              }
            />
          )}
        </>
      ),
      align: 'center',
    },
    {
      title: 'Acciones / Justificación',
      key: 'acciones',
      fixed: 'right',
      width: 200,
      render: (_, record) => {
        return (
          <Space direction={'horizontal'} style={{ width: '100%' }}>
            <IconButton
              title={
                record.requiere_eliminacion ? 'Ver Justificación' : 'Ver/Editar'
              }
              icon={<EditFilled style={{ color: IconColors.green }} />}
              onClick={() => handleEdit(record)}
            />
            {record.origen === 3 && (
              <>
                <IconButton
                  title={
                    lSwap && (record.id === swapID || record.id === lSwap)
                      ? 'Cambiando'
                      : record.id === swapID
                        ? 'Deseleccionar'
                        : 'Intercambiar'
                  }
                  disabled={
                    !!deleteID ||
                    (lSwap && (record.id === swapID || record.id === lSwap))
                  }
                  onClick={() => onSwap(record)}
                  icon={
                    lSwap && (record.id === swapID || record.id === lSwap) ? (
                      <LoadingOutlined />
                    ) : record.id === swapID ? (
                      <SwapLeftOutlined />
                    ) : swapID ? (
                      <SwapRightOutlined />
                    ) : (
                      <SwapOutlined />
                    )
                  }
                />
                <IconButton
                  title={
                    moveID && record.id === moveID ? 'Cambiando' : 'Mover a...'
                  }
                  onClick={() => onMove(record)}
                  loading={moveID && record.id === moveID}
                  disabled={!!deleteID}
                  icon={<FullscreenOutlined rotate={45} />}
                />
                <Popconfirm
                  title={MESSAGES.DELETE_CONFIRM_MSG}
                  onConfirm={async () => {
                    setDeleteID(record.id);
                    await deleteDataSource(record.id);
                    setDeleteID();
                  }}
                >
                  <IconButton
                    title="Eliminar"
                    disabled={!!deleteID}
                    icon={
                      <DeleteFilled
                        style={{ color: IconColors.red }}
                        rotate={record.id === deleteID ? 45 : 0}
                      />
                    }
                  />
                </Popconfirm>
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'origen',
      width: '8rem',
      fixed: 'right',
      render: (_, record) =>
        record.origen === 1 ? '' : record.origen === 2 ? 'Modificada' : 'Nueva',
      align: 'center',
    },
  ];

  return (
    <AjaxTable
      columns={columns}
      searchInput
      tableTitle={'Listado de responsabilidades'}
      endpoint={filterAjaxTable}
      reloadSource={reload}
      scroll={{ x: false }}
      query={ctmmID}
      exportCSV
      showSizeChanger
      pageSize={50}
    />
  );
};

const JustificationForm = memo(({ initialValues, id, onFinish }) => {
  const [loading, setLoading] = useState(false);

  const save = async (data) => {
    try {
      setLoading(true);
      await patch(id, { ...data, requiere_eliminacion: true });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setLoading(false);
      onFinish();
    } catch (err) {
      console.log(err);
      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: MESSAGES.DESC_ERROR_POST_MSG,
        duration: 2,
      });
      setLoading(false);
    }
  };

  const buttonItemLayout = {
    wrapperCol: {
      span: 10,
      offset: 11,
    },
  };

  return (
    <Form
      onFinish={save}
      autoComplete="off"
      layout="vertical"
      initialValues={initialValues}
    >
      <br />
      <Paragraph>
        Responsabilidad u obligación específica No. {initialValues.codigo}
      </Paragraph>
      <div className="ant-form-item">
        <Row gutter={300}>
          <Col>
            <Text className="ant-form-item-label">
              Responsabilidad u obligación específica de el/la contratista
              <b> vigente</b>
            </Text>
          </Col>
        </Row>
        <TextArea
          rows={4}
          allowClear
          value={initialValues.descripcion_vigente}
          disabled
        />
      </div>
      <br />
      <Form.Item
        name="justificacion"
        label="Justificación de la solicitud de eliminación"
        rules={[
          {
            required: true,
            message: 'Este campo es obligatorio!',
          },
        ]}
      >
        <TextArea rows={4} allowClear />
      </Form.Item>
      <Form.Item {...buttonItemLayout}>
        <CustomButton loading={loading} text="Guardar" />
      </Form.Item>
    </Form>
  );
});

const ResponsibilityForm = memo(
  ({ ctmmID, initialValues, id, onFinish, showOld }) => {
    const [loading, setLoading] = useState(false);

    const save = async (data) => {
      try {
        if (!id) {
          const newData = {
            modulo_modificacion: ctmmID,
            ...data,
          };
          setLoading(true);
          await post(newData);
        } else {
          setLoading(true);
          await patch(id, data);
        }
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
        setLoading(false);
        onFinish();
      } catch (err) {
        console.log(err);
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_POST_MSG,
          duration: 2,
        });
        setLoading(false);
      }
    };

    const buttonItemLayout = {
      wrapperCol: {
        span: 10,
        offset: 11,
      },
    };

    return (
      <Form
        onFinish={save}
        autoComplete="off"
        layout="vertical"
        initialValues={initialValues}
      >
        {initialValues && (
          <>
            <br />
            <Paragraph>
              Responsabilidad u obligación específica No. {initialValues.codigo}
            </Paragraph>
            {showOld && initialValues.origen !== 3 && (
              <>
                <div className="ant-form-item">
                  <Row gutter={300}>
                    <Col>
                      <Text className="ant-form-item-label">
                        Responsabilidad u obligación específica de el/la
                        contratista
                        <b> vigente</b>
                      </Text>
                    </Col>
                  </Row>
                  <TextArea
                    rows={4}
                    allowClear
                    value={initialValues.descripcion_vigente}
                    disabled
                  />
                </div>
              </>
            )}
          </>
        )}
        <br />
        <Form.Item
          name="descripcion"
          label={
            <>
              Responsabilidad u obligación específica de el/la contratista&nbsp;
              {showOld && <b>modificada</b>}
            </>
          }
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
          ]}
        >
          <TextArea rows={4} allowClear />
        </Form.Item>
        <Form.Item
          name="justificacion"
          label="Justificación"
          rules={[
            {
              required: true,
              message: 'Este campo es obligatorio!',
            },
          ]}
        >
          <TextArea rows={4} allowClear />
        </Form.Item>
        <Form.Item {...buttonItemLayout}>
          <CustomButton loading={loading} text={id ? 'Guardar' : 'Agregar'} />
        </Form.Item>
      </Form>
    );
  },
);

export default function ModificationResponsibilitiesForm({ sendPerm }) {
  const { modification_id } = useParams();
  const [dataInfo, setDataInfo] = useState();
  const [swapData, setSwapData] = useState(undefined);
  const [lSwap, setLSwap] = useState(undefined);
  const [moveData, setMoveData] = useState(undefined);
  const [loadingForm, setLoadingForm] = useState(false);

  const [reloadTable, setReloadTable] = useState();

  const [form] = Form.useForm();
  const navigate = useHistory();

  const [editData, setEditData] = useState(undefined);
  const [justificationData, setJustificationData] = useState(undefined);
  const [createNew, setCreateNew] = useState(false);

  const [openPreview, setOpenPreview] = useState(false);

  const [lRenumerate, setLRenumerate] = useState(false);

  const breadcrumbItems = [
    {
      text: 'Módulo Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}`,
    },
    {
      text: 'Formulario de Modificaciones Contractuales',
      to: `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${
        dataInfo?.proyecto_id ?? 0
      }/${modification_id}`,
    },
    {
      text: 'Responsabilidades u obligaciones específicas de el/la contratista',
    },
  ];

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      try {
        const res = await info(modification_id);
        if (mounted) setDataInfo(res.data);
      } catch (error) {
        notification.error({
          message: MESSAGES.ERROR_MSG,
          description: MESSAGES.DESC_ERROR_GET_MSG,
          duration: 2,
        });
      }
    }
    fetchData();
    return () => {
      mounted = false;
    };
  }, [modification_id]);

  const handleEdit = (record) => {
    if (record.requiere_eliminacion) {
      setJustificationData(record);
    } else {
      setEditData(record);
    }
  };

  const doReload = () => {
    setEditData(undefined);
    setReloadTable({});
  };

  const doSwap = async (a, b) => {
    try {
      setLSwap(b);
      await swap(a, b);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setSwapData(undefined);
      setLSwap(undefined);
      doReload();
    } catch (error) {
      catchException(error);
    }
  };

  const onSwap = (row) => {
    if (swapData) {
      if (row.id === swapData.id) setSwapData(undefined);
      else doSwap(swapData.id, row.id);
    } else {
      setSwapData(row);
    }
  };

  const onMove = (row) => {
    setMoveData(row);
  };

  const doMove = async (values) => {
    try {
      setMoveData((s) => ({ ...s, loading: true }));
      await move(moveData.id, values);
      setMoveData();
      setReloadTable({});
    } catch (error) {
      setMoveData((s) => ({ ...s, loading: false }));
      catchException(error);
    }
  };

  const unrequireElimination = async (id) => {
    try {
      await patch(id, { requiere_eliminacion: false, justificacion: '' });
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
      });
      setReloadTable({});
    } catch (err) {
      catchException(err);
    }
  };

  const deleteDataSource = async (id) => {
    try {
      await remove(id);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_DELETE_MSG,
      });
      if (id === swapData?.id) setSwapData(undefined);
      setReloadTable({});
    } catch (err) {
      catchException(err);
    }
  };

  const onSave = () => {
    if (sendPerm) form.submit();
    else
      navigate.push(
        `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
      );
  };

  const onFinish = async (values) => {
    try {
      setLoadingForm(true);
      await patchModule(dataInfo.id, values);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
      setTimeout(() => {
        navigate.push(
          `${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`,
        );
      }, MESSAGES.TIME);
    } catch (error) {
      catchException(error);
    } finally {
      setLoadingForm(false);
    }
  };

  const doRenumerate = async () => {
    try {
      setLRenumerate(true);
      await renumerate(modification_id);
      doReload();
      setLRenumerate(false);
      notification.success({
        message: MESSAGES.SUCCESS_MSG,
        description: MESSAGES.DESC_SUCCESS_MSG,
        duration: 2,
      });
    } catch (error) {
      catchException(error);
      setLRenumerate(false);
    }
  };

  return (
    <>
      <BreadcrumbContainer items={breadcrumbItems} />
      <div className="box-title">
        <Title className={'text-uppercase'} level={2}>
          MODIFICACIÓN CONTRACTUAL RESPONSABILIDADES
        </Title>
      </div>
      {dataInfo ? (
        <>
          <Space direction={'vertical'} style={{ width: '100%' }}>
            <Descriptions
              layout="horizontal"
              column={2}
              bordered
              labelStyle={{ maxWidth: '5rem' }}
              className="fade-in"
            >
              <Descriptions.Item label="Contratista / Proveedor" span={2}>
                <Row align="middle">
                  <Col flex="auto">{dataInfo.contratista_nombre}</Col>
                  <Col flex="0 0">
                    <CustomButton
                      htmlType="button"
                      text={
                        <Link
                          to={`${INTERNAL_LINKS.RESUME_CONTRACT_SEARCH}-detail/${dataInfo.contrato_id}/${dataInfo.proyecto_id}/${dataInfo.cargo_id}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver históricos contractuales
                        </Link>
                      }
                    />
                  </Col>
                </Row>
              </Descriptions.Item>
              <Descriptions.Item label="Cargo / Rol" span={2}>
                {dataInfo?.cargo_nombre}
              </Descriptions.Item>
            </Descriptions>
          </Space>
          <br />
          <br />

          <Card
            size={'small'}
            bordered={false}
            title={
              <Title level={3}>
                2. Ajuste de responsabilidades u obligaciones específicas:
              </Title>
            }
          >
            {sendPerm && (
              <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                onFinish={onFinish}
                initialValues={{
                  clausula: dataInfo.clausula,
                }}
              >
                <Form.Item
                  label="Digite en letras el número de la cláusula del contrato a modificar"
                  name="clausula"
                  rules={[
                    {
                      required: true,
                      message: 'Ingrese el nombre de la cláusula',
                    },
                    {
                      max: 100,
                      message:
                        'El campo debe tener un máximo de 100 caracteres',
                    },
                  ]}
                >
                  <Input
                    type="text"
                    allowClear
                    placeholder=""
                    name="name_clause"
                  />
                </Form.Item>
              </Form>
            )}
          </Card>
          <Paragraph>
            Por favor, de las siguientes obligaciones modifique las que requiera
          </Paragraph>
          <CustomButton
            htmlType="button"
            text="Renumerar"
            onClick={doRenumerate}
            loading={lRenumerate}
          />
          {swapData && (
            <>
              <Alert
                message="Intercambio de código"
                description={
                  <>
                    Se ha seleccionado para intercambio de código el item
                    <br />
                    <br />
                    Código {swapData.codigo}: {swapData.descripcion}
                  </>
                }
                type={'info'}
                showIcon
              />
            </>
          )}

          <ResponsibilitiesList
            ctmmID={dataInfo.id}
            handleEdit={handleEdit}
            deleteDataSource={deleteDataSource}
            reload={reloadTable}
            onMove={onMove}
            moveID={moveData?.id}
            onSwap={onSwap}
            swapID={swapData?.id}
            lSwap={lSwap}
            requireElimination={(record) => setJustificationData(record)}
            unrequireElimination={(record) => unrequireElimination(record.id)}
          />

          <Paragraph>
            Para agregar una nueva responsabilidad u obligación de clic en el
            botón "Agregar"{' '}
            <CustomButton
              htmlType="button"
              text="Agregar"
              onClick={() => setCreateNew(true)}
            />
          </Paragraph>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={!!editData}
            onCancel={() => setEditData(undefined)}
            width={'80%'}
          >
            <b>Modificación de responsabilidad u obligación específica</b>
            <ResponsibilityForm
              ctmmID={dataInfo.id}
              initialValues={editData}
              onFinish={() => {
                doReload();
                setCreateNew(false);
              }}
              id={editData?.id}
              showOld={editData?.origen === 1 || editData?.origen === 2}
            />
          </Modal>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={!!justificationData}
            onCancel={() => setJustificationData(undefined)}
            width={'80%'}
          >
            <b>Solicitud de eliminación</b>
            <JustificationForm
              initialValues={justificationData}
              onFinish={() => {
                doReload();
                setJustificationData(undefined);
              }}
              id={justificationData?.id}
            />
          </Modal>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={createNew}
            onCancel={() => setCreateNew(false)}
            width={'80%'}
          >
            <b>Agregar una nueva responsabilidad u obligación específica</b>
            <ResponsibilityForm
              ctmmID={dataInfo.id}
              onFinish={() => {
                doReload();
                setCreateNew(false);
              }}
            />
          </Modal>

          <Modal
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Modificación Contractual
              </Title>
            }
            destroyOnClose={true}
            footer={null}
            open={openPreview}
            onCancel={() => setOpenPreview(false)}
            width={'80%'}
          >
            <b>Previsualización de responsabilidades u obligaciones</b>
            <AjaxTable
              columns={[
                {
                  title: 'Código',
                  dataIndex: 'codigo',
                },
                {
                  title: 'Obligación',
                  dataIndex: 'descripcion',
                  key: 'descripcion',
                },
              ]}
              searchInput
              tableTitle={'Listado de responsabilidades'}
              endpoint={preview}
              scroll={{ x: false }}
              query={dataInfo.id}
              exportCSV
              showSizeChanger
              pageSize={50}
            />
          </Modal>

          <Modal
            open={moveData?.id}
            title={
              <Title level={4} style={{ marginBottom: 0 }}>
                Mover a...
              </Title>
            }
            footer={null}
            closable
            onCancel={() => setMoveData()}
            destroyOnClose
          >
            <Form onFinish={doMove}>
              <Form.Item
                name="new_code"
                label="Nuevo código"
                rules={[
                  { required: true, message: 'El nuevo código es requerido' },
                  {
                    type: 'number',
                    min: 1,
                    message: 'Ingrese un valor positivo',
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
              <Space align="end">
                <CustomButton
                  htmlType="button"
                  className={'back-button'}
                  type="default"
                  text="Volver"
                  onClick={() => setMoveData()}
                />
                <CustomButton
                  loading={moveData?.loading}
                  htmlType="submit"
                  text="Mover"
                />
              </Space>
            </Form>
          </Modal>

          <Space size="large">
            <CustomButton
              htmlType="button"
              className={'back-button'}
              type="default"
              text={
                <Link
                  to={`${INTERNAL_LINKS.CONTRACT_MODIFICATIONS}-form/${dataInfo.proyecto_id}/${modification_id}`}
                >
                  Volver
                </Link>
              }
            />

            <CustomButton
              htmlType="button"
              type="default"
              text="Previsualizar cambios"
              onClick={() => setOpenPreview(true)}
            />

            <ModificationModuleSaveButton
              onConfirm={onSave}
              loading={loadingForm}
            />
          </Space>
        </>
      ) : (
        <Skeleton active></Skeleton>
      )}
    </>
  );
}
